import { useState } from 'react';
import { useNavigate } from 'react-router';

import { BASENAME_URL_AUTH_ADFS } from 'constants/settings';

import { apiAuthServices } from 'services/api/auth';

import { useUserStore } from 'stores/user';

type TUseAuthParams = {
    /** Shows a translation id under the username. */
    setUsernameError: (messageId: string) => void;
    /** Shows a translation id under the password. */
    setPasswordError: (messageId: string) => void;
};

const useAuth = ({ setUsernameError, setPasswordError }: TUseAuthParams) => {
    const navigate = useNavigate();
    const { handleLoginSuccess, handleLoginFailure } = useUserStore((state) => state.actions);

    const [requesting, setRequesting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const refusePassword = () => {
        setPasswordError('label.password.error');
        handleLoginFailure();
    };

    const refuseUsername = () => {
        setUsernameError('label.username.error');
        handleLoginFailure();
    };

    const login = async ({ username, password }: { username: string; password: string }) => {
        try {
            setRequesting(true);
            const token = await apiAuthServices.login(username, password);
            if (token) {
                handleLoginSuccess(token);
            } else {
                refusePassword();
            }
        } catch {
            // A failed request was reported by the service.
            refusePassword();
        } finally {
            setRequesting(false);
        }
    };

    const checkSSOUser = async (username: string) => {
        try {
            setRequesting(true);
            const authModes = await apiAuthServices.getAuthModes(username);
            if (!authModes) {
                refuseUsername();
            } else if (authModes.login_by_sso) {
                navigate(BASENAME_URL_AUTH_ADFS);
            } else {
                setShowPassword(true);
            }
        } catch {
            // A failed request was reported by the service.
            refuseUsername();
        } finally {
            setRequesting(false);
        }
    };

    const handleChangeUsername = () => {
        setShowPassword(false);
    };

    return {
        requesting,
        showPassword,
        login,
        checkSSOUser,
        handleChangeUsername,
    };
};

export default useAuth;
